/* iter55i — Marketing & Business panel (Phase 4).
 *
 * Renders the Microsoft Clarity heat-map dashboard iframe when the
 * admin has both (a) configured a Clarity project ID and (b) pasted
 * a shareable embed URL from Clarity's Share → Get embed link. If
 * the embed URL is missing, we surface a friendly hint and a direct
 * link to the Clarity dashboard.
 *
 * Basic-tier users see an upgrade CTA card instead of the heatmap.
 *
 * Feature gate: `marketing_panel` capability (unlocked on Pro Author
 * and Premier Studio).
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PanelFrame } from '../workspace/PanelFrame';
import { TrendingUp, Lock, ExternalLink, RefreshCw, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { clarityApi } from '@/lib/api';
import { useCapabilities } from '@/lib/useCapabilities';
import StorefrontMarketingInsights from './StorefrontMarketingInsights';
import SocialListeningInsights from './SocialListeningInsights';

export function MarketingPanel({ onClose, maximized, onMaximize }) {
  const { t } = useTranslation();
  const { can, upgradeCta } = useCapabilities();
  const allowed = can('marketing_panel');
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    clarityApi.publicConfig()
      .then((r) => setCfg(r))
      .catch(() => setCfg(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!allowed) { setLoading(false); return; }
    load();
  }, [allowed]);

  return (
    <PanelFrame
      title={t('marketing.panelTitle')}
      icon={TrendingUp}
      onClose={onClose}
      onMaximize={onMaximize}
      maximized={maximized}
      testId="marketing-panel"
    >
      {allowed && loading ? (
        <div className="p-6 text-[13.5px] text-muted-foreground" data-testid="marketing-loading">
          {t('marketing.loadingHeatmap')}
        </div>
      ) : (
        <MarketingBody cfg={cfg} onRefresh={load} clarityAllowed={allowed} cta={upgradeCta('marketing_panel')} t={t} />
      )}
    </PanelFrame>
  );
}

function UpgradeCta({ cta, t }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center"
         data-testid="marketing-upgrade-cta">
      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary">
        <Lock className="h-5 w-5 text-muted-foreground" />
      </div>
      <div>
        <div className="text-[13px] font-semibold">{cta.title}</div>
        <div className="mt-1 max-w-xs text-[13px] text-muted-foreground">
          {cta.body} {t('marketing.upgradeHint')}
        </div>
      </div>
      <ul className="mt-2 space-y-1 text-left text-[13px] text-muted-foreground">
        <li>· {t('marketing.featureHeatmaps')}</li>
        <li>· {t('marketing.featureSessions')}</li>
        <li>· {t('marketing.featureFunnels')}</li>
      </ul>
    </div>
  );
}

function MarketingBody({ cfg, onRefresh, clarityAllowed, cta, t }) {
  const configured = Boolean(cfg?.project_id);
  const embed = cfg?.dashboard_embed_url;
  return (
    <div className="h-full overflow-y-auto" data-testid="marketing-body">
      <SocialListeningInsights />
      <StorefrontMarketingInsights />
      {!clarityAllowed ? <div className="border-t border-border"><UpgradeCta cta={cta} t={t} /></div> : !configured ? <div className="flex min-h-64 flex-col items-center justify-center gap-3 border-t border-border p-6 text-center"
           data-testid="marketing-not-configured">
        <AlertTriangle className="h-5 w-5 text-amber-500" />
        <div className="text-[13px] font-semibold">{t('marketing.notConfiguredTitle')}</div>
        <div className="max-w-xs text-[13px] text-muted-foreground">
          {t('marketing.notConfiguredBody1')}
          <span className="mx-1 font-medium">Admin → Marketing (Clarity)</span>
          {t('marketing.notConfiguredBody2')}
        </div>
        <Button size="sm" variant="outline" onClick={onRefresh}
                data-testid="marketing-refresh-btn"
                className="h-7 gap-1 text-[13px]">
          <RefreshCw className="h-3 w-3" /> {t('marketing.retry')}
        </Button>
      </div> : !embed ? <div className="flex min-h-64 flex-col items-center justify-center gap-3 border-t border-border p-6 text-center"
           data-testid="marketing-no-embed">
        <TrendingUp className="h-5 w-5 text-primary" />
        <div className="text-[13px] font-semibold">{t('marketing.collectingTitle')}</div>
        <div className="max-w-xs text-[13px] text-muted-foreground">
          {t('marketing.collectingBody1')}
          <span className="mx-1 rounded bg-secondary px-1 font-mono">{cfg.project_id}</span>.
          {' '}{t('marketing.collectingBody2')}
        </div>
        <a
          href={`https://clarity.microsoft.com/projects/view/${cfg.project_id}/dashboard`}
          target="_blank" rel="noopener noreferrer"
          data-testid="marketing-open-clarity-link"
          className="inline-flex items-center gap-1 rounded border border-border px-3 py-1 text-[13px] hover:bg-secondary"
        >
          <ExternalLink className="h-3 w-3" /> {t('marketing.openClarityDashboard')}
        </a>
      </div> : <div className="flex min-h-[480px] flex-col border-t border-border" data-testid="marketing-embed-wrap">
      <div className="flex items-center justify-between border-b border-panel-border bg-panel-header px-3 py-1.5">
        <div className="text-[13px] text-muted-foreground">
          Clarity · <span className="font-mono">{cfg.project_id}</span>
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={onRefresh}
                  data-testid="marketing-refresh-btn"
                  className="h-6 px-2 text-[13px]">
            <RefreshCw className="mr-1 h-3 w-3" /> {t('marketing.refresh')}
          </Button>
          <a
            href={embed}
            target="_blank" rel="noopener noreferrer"
            data-testid="marketing-open-clarity-link"
            className="inline-flex items-center gap-1 rounded border border-border px-2 py-0.5 text-[13px] hover:bg-secondary"
          >
            <ExternalLink className="h-3 w-3" /> {t('marketing.openInClarity')}
          </a>
        </div>
      </div>
      <div className="flex-1 min-h-0 bg-background">
        <iframe
          src={embed}
          title="Microsoft Clarity heat-maps"
          data-testid="marketing-clarity-iframe"
          className="h-full w-full border-0"
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
        />
      </div>
    </div>}
    </div>
  );
}
